import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { invitationApi } from '@/services/roleApi';
import { useAuth } from '@/hooks/useAuth';

const AcceptInvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ name: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: invitationData, isLoading, error } = useQuery({
    queryKey: ['invitation-details', token],
    queryFn: () => invitationApi.getDetails(token!).then(res => res.data.data),
    enabled: !!token,
    retry: false,
  });

  const acceptNewUserMutation = useMutation({
    mutationFn: (data: { name: string; password: string }) =>
      invitationApi.accept(token!, data),
    onSuccess: () => {
      toast.success('Account created! Please log in.');
      navigate('/login', {
        state: { message: 'Account created. Please log in to continue.', email: invitationData?.email },
      });
    },
    onError: (error: any) => {
      const code = error.response?.data?.code;
      if (code === 'USER_EXISTS') {
        toast('An account already exists. Please log in to join the organisation.', { icon: 'ℹ️' });
        navigate('/login', {
          state: { message: 'Please log in to join the organisation.', returnTo: `/accept-invitation/${token}` },
        });
      } else {
        toast.error(error.response?.data?.message || 'Failed to accept invitation');
      }
    },
  });

  const acceptExistingUserMutation = useMutation({
    mutationFn: () => invitationApi.acceptAsMember(token!),
    onSuccess: (res) => {
      toast.success(res.data.message || 'Successfully joined organisation!');
      navigate('/app/organization');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to join organisation');
    },
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim() || formData.name.trim().length < 2) newErrors.name = 'Name must be at least 2 characters';
    if (!formData.password || formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) newErrors.password = 'Must contain uppercase, lowercase, and number';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNewUserSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) acceptNewUserMutation.mutate({ name: formData.name.trim(), password: formData.password });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Loading invitation details…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !invitationData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto" />
            <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">Invalid Invitation</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              This invitation link is invalid or has expired. Please contact your organisation administrator.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const InvitationDetails = () => (
    <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
      <div className="flex items-center gap-2 mb-2">
        <BuildingOfficeIcon className="h-4 w-4 text-blue-700 dark:text-blue-400" />
        <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300">Invitation Details</h3>
      </div>
      <div className="space-y-1 text-sm text-blue-800 dark:text-blue-300">
        <p><span className="font-medium">Organisation:</span> {invitationData.organizationName}</p>
        <p><span className="font-medium">Role:</span> {invitationData.roleName}</p>
        <p><span className="font-medium">Invited by:</span> {invitationData.inviterName}</p>
        <p><span className="font-medium">Expires:</span> {formatDate(invitationData.expiresAt)}</p>
      </div>
      {invitationData.message && (
        <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border border-blue-200 dark:border-blue-800">
          <p className="text-sm font-medium text-blue-900 dark:text-blue-300">Personal message:</p>
          <p className="text-sm text-blue-800 dark:text-blue-300 mt-1">"{invitationData.message}"</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <CheckCircleIcon className="h-12 w-12 text-green-600" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900 dark:text-white">
          Join {invitationData.organizationName}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          You've been invited as a <span className="font-medium text-blue-600 dark:text-blue-400">{invitationData.roleName}</span>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <InvitationDetails />

          {/* Existing authenticated user — one-click join */}
          {isAuthenticated && user?.email === invitationData.email ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                You're logged in as <strong>{user?.email}</strong>. Click below to join the organisation.
              </p>
              <button
                onClick={() => acceptExistingUserMutation.mutate()}
                disabled={acceptExistingUserMutation.isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {acceptExistingUserMutation.isLoading ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Joining…</>
                ) : (
                  `Join ${invitationData.organizationName}`
                )}
              </button>
              <button
                onClick={() => navigate('/app/dashboard')}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Decline
              </button>
            </div>
          ) : isAuthenticated ? (
            /* Logged in as wrong user */
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg text-sm text-amber-800 dark:text-amber-300">
                You are logged in as <strong>{user?.email}</strong>, but this invitation was sent to <strong>{invitationData.email}</strong>. Please log in with the correct account.
              </div>
              <button
                onClick={() => navigate('/login', { state: { returnTo: `/accept-invitation/${token}` } })}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Switch Account
              </button>
            </div>
          ) : (
            /* Not logged in — show signup form */
            <>
              <form onSubmit={handleNewUserSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email Address</label>
                  <input
                    id="email" type="email" disabled
                    value={invitationData.email}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 sm:text-sm cursor-not-allowed"
                    title="This email is pre-filled from your invitation"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Email is pre-filled from your invitation</p>
                </div>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Full Name *</label>
                  <input
                    id="name" type="text" required
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white ${errors.name ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password *</label>
                  <div className="mt-1 relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white ${errors.password ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                      placeholder="Create a strong password"
                    />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(s => !s)}>
                      {showPassword ? <EyeSlashIcon className="h-5 w-5 text-gray-400" /> : <EyeIcon className="h-5 w-5 text-gray-400" />}
                    </button>
                  </div>
                  {errors.password && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password}</p>}
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">At least 8 characters with uppercase, lowercase, and number</p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm Password *</label>
                  <input
                    id="confirmPassword" type="password" required
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white ${errors.confirmPassword ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                    placeholder="Confirm your password"
                  />
                  {errors.confirmPassword && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmPassword}</p>}
                </div>

                <button
                  type="submit"
                  disabled={acceptNewUserMutation.isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {acceptNewUserMutation.isLoading ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Creating Account…</>
                  ) : 'Accept Invitation & Create Account'}
                </button>
              </form>

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300 dark:border-gray-600" /></div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">Already have an account?</span>
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => navigate('/login', { state: { returnTo: `/accept-invitation/${token}` } })}
                    className="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Sign in instead
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitationPage;
